/**
 * Générateur — Attendu 954 — Connaître la somme des angles d'un triangle et
 * savoir la démontrer.
 *
 * Niveau : 5ème | Thème #10 Triangles (O13) | Domaine : Espace et géométrie
 * Source prof : « TANOED EXERCISEUR espace et géométrie.pdf » (cluster 1,
 * Ex.1-12 + calcul), extraction exerciseur/triangles.
 *
 * 9 cartes (figures EXACTES par construction, svgTriangles) :
 *  1. demo      — Ex.1 : démonstration par les alternes-internes (droite //
 *                 par le sommet). Trous à menus + parallélisme. Déclarative.
 *  2. calc_fig  — Ex.2 : deux angles marqués sur la figure → le 3e (saisie).
 *  3. calc_sans — Ex.3 : deux angles dans l'énoncé (sans figure) → le 3e ;
 *                 inclut les décimaux et un triangle très aplati.
 *  4. partic    — Ex.4 : cas rectangle / isocèle (base) / isocèle (sommet) ;
 *                 saisie + QCM justification (le contraste b/c du doc).
 *  5. iso       — Ex.8 : tableau des isocèles (angle à la base OU au sommet
 *                 donné → les deux autres). Saisie à trous.
 *  6. tableur   — Ex.5 : calcul du 3e angle (saisie) + formule du tableur
 *                 (QCM — flag correcteur du doc : =180-(B1+B2) équivalent).
 *  7. chaine    — Ex.6/7/11 : A, D, B alignés, deux angles codés égaux en C →
 *                 calcul en chaîne de l'angle ABC (figure construite).
 *  8. equation  — Ex.12 : R̂ = a, Ŝ = a + k, deux angles codés égaux →
 *                 3a + k = 180 (croisement calcul littéral).
 *  9. align     — Ex.9 : le pari d'Aline — les trois angles en E font-ils
 *                 180° ? (alignement décidable strictement, QCM justifié).
 *
 * INVARIANTS CLÉS (par construction) :
 *  - calc_fig : angles ENTIERS, 3e = 180 − a − b (le solveur lit les deux
 *    mesures marquées « mv » et rend 180 − somme) ; figure réalise les angles.
 *  - calc_sans : décimaux au demi-degré (a + b entier), triangle aplati
 *    possible (3e ≥ 1°) — solveur lit l'énoncé (décimaux gérés).
 *  - partic : a PAIR → 90−a, a et (180−a)/2 entiers ; règles rectangle /
 *    isocèle base / isocèle sommet toutes réalisées sur la figure.
 *  - chaine : α ∈ [30,45], β ∈ [78,92] tels que γ₁=180−α−β ≥ 20 et
 *    réponse = 2β+α−180 ≥ 5 ; figure EXACTE (loi des sinus pour placer B).
 *  - equation : k ∈ {9,12,…,24} → a = (180−k)/3 entier ; angles R̂ = T̂ codés.
 *  - align : alignés ⟺ a₁+a₂+a₃ = 180 (sinon ±2..4) — jamais « à l'œil ».
 *
 * Solveurs (tests/solvers/triangles.js) : calc_fig, calc_sans, partic (+QCM),
 * iso, tableur (+QCM), chaine, equation, align re-dérivés du TEXTE/FIGURE ;
 * la carte demo (démonstration guidée) est déclarative.
 */

import {
    randInt, randomChoice, shuffle,
    makeSaisie, makeQcm, makeTexteTrou, filtrerDistracteurs,
} from '../../../utils';
import {
    creerFigure, ptAt, inter, angleVers, dist,
    triangleParAngles, marqueInterieur, codageDroitInterieur, cadreur, figEl,
    BLEU, ROUGE, VERT, chapeau, nomAngle,
} from './svgTriangles';

// ═══════════════════════════════════════════════════════════
// CONSTANTES
// ═══════════════════════════════════════════════════════════

const ATTENDU_ID = 954;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_DEMO = 'Démontrer la somme des angles (180°)';
const L_CALC_FIG = 'Calculer l\'angle manquant (avec figure)';
const L_CALC_SANS = 'Calculer l\'angle manquant (sans figure)';
const L_PARTIC = 'Triangle rectangle ou isocèle';
const L_ISO = 'Tableau des triangles isocèles';
const L_TABLEUR = 'Les formules du tableur';
const L_CHAINE = 'Calculs en chaîne sur une figure';
const L_EQUATION = 'Angles et équation';
const L_ALIGN = 'Ces points sont-ils alignés ?';

const VB = { w: 340, h: 250 };

const salt = (...n) => n.map((x) => Math.round(x)).join('_');
const pickLetters = (n) => shuffle('ABCDEFGHJKLMNPRSTUVWXYZ'.split('')).slice(0, n);
const frDeg = (x) => (Number.isInteger(x) ? String(x) : String(x).replace('.', ','));
const centroid = (A, B, C) => ({ x: (A.x + B.x + C.x) / 3, y: (A.y + B.y + C.y) / 3 });

/** Trace les 3 côtés + les 3 sommets nommés (étiquettes vers l'extérieur). */
const drawTriangle = (fig, A, B, C, nA, nB, nC) => {
    fig.segment(A, B);
    fig.segment(B, C);
    fig.segment(C, A);
    const G = centroid(A, B, C);
    fig.point(A, nA, angleVers(G, A), 14);
    fig.point(B, nB, angleVers(G, B), 14);
    fig.point(C, nC, angleVers(G, C), 14);
};

/** Construit un triangle d'angles (angA en A, angB en B) et le cadre dans VB. */
const triangleCadre = (angA, angB) => {
    const raw = triangleParAngles(angA, angB, 200, { x: 0, y: 0 });
    const cad = cadreur([raw.A, raw.B, raw.C], VB);
    return { A: cad(raw.A), B: cad(raw.B), C: cad(raw.C) };
};

/** Double arc (sans mesure) codant deux angles égaux. */
const marqueEgal = (fig, V, P, Q, couleur = BLEU) => {
    marqueInterieur(fig, V, P, Q, '', { couleur, r: 12 });
    marqueInterieur(fig, V, P, Q, '', { couleur, r: 15 });
};

// ═══════════════════════════════════════════════════════════
// Carte 1 — Démonstration par les alternes-internes (Ex.1) — DÉCLARATIVE
// ═══════════════════════════════════════════════════════════

const genDemo = () => {
    const a = randInt(46, 74);
    const b = randInt(40, 70);
    // 5 lettres DISTINCTES : 3 sommets du triangle + 2 extrémités de la parallèle.
    // (Les extrémités ne peuvent plus jamais entrer en collision avec un sommet,
    //  sinon un même nom désignerait deux points → démonstration ambiguë.)
    const [nA, nB, nC, nL, nR] = pickLetters(5);
    const { A, B, C } = triangleCadre(a, b);
    const fig = creerFigure(VB, salt(a, b, randInt(0, 9999)));
    // Droite (nL nR) par C, parallèle à (AB) : nL à GAUCHE, nR à DROITE
    // (A est en bas-à-gauche, B en bas-à-droite par construction de triangleParAngles).
    const PL = ptAt(C, 180, 150);
    const PR = ptAt(C, 0, 150);
    fig.segment(PL, PR, 'para');
    drawTriangle(fig, A, B, C, nA, nB, nC);
    fig.point(PL, nL, 180, 12);
    fig.point(PR, nR, 0, 12);
    // Angles alternes-internes (verts) + les 3 angles au sommet C.
    marqueInterieur(fig, A, B, C, `${a}°`, { couleur: VERT, r: 18 });
    marqueInterieur(fig, B, C, A, `${b}°`, { couleur: VERT, r: 18 });

    // nomAngle place TOUJOURS le chapeau sur le sommet (lettre du milieu).
    // Angle en A ⟷ rayon parallèle GAUCHE ; angle en B ⟷ rayon DROIT ; plat = nL–nC–nR.
    const phrase = `Les angles en ${nA} et ${nomAngle(nL, nC, nA)} sont {{alternes-internes}} : ils sont {{égaux}}. `
        + `Les angles en ${nB} et ${nomAngle(nB, nC, nR)} sont aussi {{alternes-internes}} : ils sont {{égaux}}. `
        + `Or ${nomAngle(nL, nC, nR)} est un angle {{plat}}, donc la somme des trois angles au sommet ${nC} vaut {{180}}°. `
        + `On en déduit que ${nA} + ${nB} + ${nC} = {{180}}°.`;
    const distracteurs = filtrerDistracteurs(phrase, [
        'correspondants', 'opposés', 'différents', 'droit', '90', '360',
    ]);
    const texte = `<p>On trace la droite (${nL}${nR}) passant par ${nC}, parallèle au côté (${nA}${nB}) du triangle ${nA}${nB}${nC}. `
        + 'Complète la démonstration de « la somme des angles d\'un triangle vaut 180° » :</p>';
    const payload = makeTexteTrou(phrase, texte, {
        ...opts(L_DEMO), mode: 'drag', distracteurs, strict: false,
        elements_additionnels: figEl(fig.render()),
    });
    payload._meta.generatorKey = 'demo_texte_trou_drag';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Carte 2 — Calculer l'angle manquant, avec figure (Ex.2)
// ═══════════════════════════════════════════════════════════

const tirAngles2 = () => {
    for (let i = 0; i < 200; i++) {
        const a = randInt(30, 110);
        const b = randInt(30, 110);
        const c = 180 - a - b;
        if (c >= 25 && a <= 120 && b <= 120 && new Set([a, b, c]).size === 3) return { a, b, c };
    }
    return { a: 60, b: 70, c: 50 };
};

const genCalcFig = () => {
    const { a, b, c } = tirAngles2();
    const [nA, nB, nC] = pickLetters(3);
    const { A, B, C } = triangleCadre(a, b);
    const fig = creerFigure(VB, salt(a, b, randInt(0, 9999)));
    drawTriangle(fig, A, B, C, nA, nB, nC);
    const angs = { [nA]: a, [nB]: b, [nC]: c };
    const noms = [nA, nB, nC];
    const cache = randomChoice(noms);
    const marque = (V, P, Q, nom) => marqueInterieur(fig, V, P, Q,
        nom === cache ? '?' : `${angs[nom]}°`,
        { couleur: nom === cache ? ROUGE : VERT, r: 18 });
    marque(A, B, C, nA);
    marque(B, C, A, nB);
    marque(C, A, B, nC);

    const texte = '<p>Deux angles de ce triangle sont donnés. Calcule la mesure de l\'angle marqué « ? ».</p>';
    const payload = makeSaisie(String(angs[cache]), texte, {
        ...opts(L_CALC_FIG), strict: true, suffixe: '°',
        elements_additionnels: figEl(fig.render()),
    });
    payload._meta.generatorKey = 'calc_fig_texte';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Carte 3 — Calculer l'angle manquant, sans figure (Ex.3)
// ═══════════════════════════════════════════════════════════

const tirAnglesSans = () => {
    const mode = randomChoice(['ent', 'ent', 'dec', 'plat']);
    if (mode === 'dec') {
        const a = randInt(10, 60) + 0.5;
        const b = randInt(10, 60) + 0.5;      // a + b entier → c entier
        return { a, b, c: 180 - a - b };
    }
    if (mode === 'plat') {
        const b = randInt(150, 168) + 0.5;
        const a = randInt(3, 10) + 0.5;       // a + b entier, c petit (triangle aplati)
        return { a, b, c: 180 - a - b };
    }
    for (let i = 0; i < 200; i++) {
        const a = randInt(25, 120);
        const b = randInt(25, 120);
        const c = 180 - a - b;
        if (c >= 20 && new Set([a, b, c]).size === 3) return { a, b, c };
    }
    return { a: 44, b: 40, c: 96 };
};

const genCalcSans = () => {
    const { a, b, c } = tirAnglesSans();
    const [nA, nB, nC] = pickLetters(3);
    const texte = `<p>${nA}${nB}${nC} est un triangle tel que l'angle en ${nA} mesure ${frDeg(a)}° `
        + `et l'angle en ${nB} mesure ${frDeg(b)}°. Calcule la mesure de l'angle en ${nC} (sans figure).</p>`;
    const payload = makeSaisie(frDeg(c), texte, {
        ...opts(L_CALC_SANS), strict: true, suffixe: '°',
    });
    payload._meta.generatorKey = 'calc_sans_texte';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Carte 4 — Cas particuliers rectangle / isocèle (Ex.4)
// ═══════════════════════════════════════════════════════════

// Sommets O (angle donné), U (angle cherché), I (3e). Régimes réalisés
// exactement sur la figure ; réponses entières (a pair).
const genPartic = () => {
    const [nO, nU, nI] = pickLetters(3);
    const a = randomChoice([40, 44, 48, 52, 56, 58, 62, 64, 68, 72, 76]);
    const regime = randomChoice(['rect', 'iso_base', 'iso_apex']);
    let rep, propriete, fig;

    if (regime === 'rect') {
        rep = 90 - a;                                   // I = 90 → U = 90 − a
        const { A, B, C } = triangleCadre(a, rep);      // A=O(a), B=U(rep), C=I(90)
        fig = creerFigure(VB, salt(a, 1, randInt(0, 9999)));
        drawTriangle(fig, A, B, C, nO, nU, nI);
        codageDroitInterieur(fig, C, A, B, { couleur: BLEU });
        marqueInterieur(fig, A, B, C, `${a}°`, { couleur: VERT, r: 18 });
        marqueInterieur(fig, B, C, A, '?', { couleur: ROUGE, r: 18 });
        propriete = 'Dans un triangle rectangle, les deux angles aigus ont pour somme 90°.';
    } else if (regime === 'iso_base') {
        rep = a;                                        // isocèle en I → angles à la base égaux
        const { A, B, C } = triangleCadre(a, a);        // A=O(a), B=U(a), C=I(apex)
        fig = creerFigure(VB, salt(a, 2, randInt(0, 9999)));
        drawTriangle(fig, A, B, C, nO, nU, nI);
        fig.codageLongueur(C, A);
        fig.codageLongueur(C, B);
        marqueInterieur(fig, A, B, C, `${a}°`, { couleur: VERT, r: 18 });
        marqueInterieur(fig, B, C, A, '?', { couleur: ROUGE, r: 18 });
        propriete = 'Les angles à la base d\'un triangle isocèle sont égaux.';
    } else {
        rep = (180 - a) / 2;                            // isocèle en O → base = (180−a)/2
        const { A, B, C } = triangleCadre(a, rep);      // A=O(apex a), B=U(rep), C=I(rep)
        fig = creerFigure(VB, salt(a, 3, randInt(0, 9999)));
        drawTriangle(fig, A, B, C, nO, nU, nI);
        fig.codageLongueur(A, B);
        fig.codageLongueur(A, C);
        marqueInterieur(fig, A, B, C, `${a}°`, { couleur: VERT, r: 18 });
        marqueInterieur(fig, B, C, A, '?', { couleur: ROUGE, r: 18 });
        propriete = 'La somme des angles vaut 180° et les deux angles à la base d\'un isocèle sont égaux.';
    }

    const nature = regime === 'rect' ? `rectangle en ${nI}`
        : (regime === 'iso_base' ? `isocèle en ${nI}` : `isocèle en ${nO}`);
    return {
        nO, nU, nI, a, rep, regime, propriete, nature, fig,
    };
};

const genParticSaisie = () => {
    const { nO, nU, nI, a, rep, nature, fig } = genPartic();
    const texte = `<p>Le triangle ${nO}${nU}${nI} est ${nature}. L'angle en ${nO} mesure ${a}°. `
        + `Calcule la mesure de l'angle en ${nU} (marqué « ? »).</p>`;
    const payload = makeSaisie(String(rep), texte, {
        ...opts(L_PARTIC), strict: true, suffixe: '°',
        elements_additionnels: figEl(fig.render()),
    });
    payload._meta.generatorKey = 'partic_texte';
    return payload;
};

const PROPRIETES_PARTIC = [
    'Dans un triangle rectangle, les deux angles aigus ont pour somme 90°.',
    'Les angles à la base d\'un triangle isocèle sont égaux.',
    'La somme des angles vaut 180° et les deux angles à la base d\'un isocèle sont égaux.',
    'Dans un triangle, tous les angles sont toujours égaux.',
];

const genParticQcm = () => {
    const { nO, nU, a, nature, propriete, fig } = genPartic();
    const texte = `<p>Le triangle est ${nature} et l'angle en ${nO} mesure ${a}°. `
        + `Quelle propriété permet de calculer l'angle en ${nU} ?</p>`;
    const payload = makeQcm(PROPRIETES_PARTIC, propriete, texte, {
        ...opts(L_PARTIC),
        elements_additionnels: figEl(fig.render()),
    });
    payload._meta.generatorKey = 'partic_qcm_unique';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Carte 5 — Tableau des triangles isocèles (Ex.8)
// ═══════════════════════════════════════════════════════════

const figIsocele = (apexAng, baseAng, nX, nY, nS, given, s) => {
    // triangleParAngles(baseAng, baseAng) : A,B base ; C sommet principal (apex).
    const { A, B, C } = triangleCadre(baseAng, baseAng);
    const fig = creerFigure(VB, s);
    drawTriangle(fig, A, B, C, nX, nY, nS);
    fig.codageLongueur(C, A);
    fig.codageLongueur(C, B);
    if (given === 'base') marqueInterieur(fig, A, B, C, `${frDeg(baseAng)}°`, { couleur: VERT, r: 16 });
    else marqueInterieur(fig, C, A, B, `${frDeg(apexAng)}°`, { couleur: VERT, r: 16 });
    return fig;
};

const genIso = () => {
    const [nX, nY, nS] = pickLetters(3);
    const regime = randomChoice(['base', 'apex']);
    let phrase, apexAng, baseAng;
    if (regime === 'base') {
        baseAng = randInt(25, 75);
        apexAng = 180 - 2 * baseAng;
        phrase = `${nX}${nY}${nS} est isocèle en ${nS}. L'angle en ${nX} mesure ${baseAng}°. `
            + `Alors l'angle en ${nY} mesure {{${baseAng}}}° et l'angle en ${nS} mesure {{${apexAng}}}°.`;
    } else {
        apexAng = randInt(40, 120);
        baseAng = (180 - apexAng) / 2;
        phrase = `${nX}${nY}${nS} est isocèle en ${nS}. L'angle en ${nS} mesure ${apexAng}°. `
            + `Alors les angles en ${nX} et ${nY} mesurent chacun {{${frDeg(baseAng)}}}°.`;
    }
    const fig = figIsocele(apexAng, baseAng, nX, nY, nS,
        regime === 'base' ? 'base' : 'apex', salt(apexAng, baseAng, randInt(0, 9999)));
    const texte = '<p>Complète les mesures manquantes dans ce triangle isocèle.</p>';
    const payload = makeTexteTrou(phrase, texte, {
        ...opts(L_ISO), mode: 'saisie', strict: false,
        elements_additionnels: figEl(fig.render()),
    });
    payload._meta.generatorKey = 'iso_texte_trou_saisie';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Carte 6 — Le tableur (Ex.5)
// ═══════════════════════════════════════════════════════════

const genTableurSaisie = () => {
    const [nK, nL, nM] = pickLetters(3);
    let angL, angM, angK;
    for (let i = 0; i < 200; i++) {
        angL = randInt(40, 85);
        angM = randInt(40, 85);
        angK = 180 - angL - angM;
        if (angK >= 20 && angK <= 100 && new Set([angK, angL, angM]).size === 3) break;
    }
    const texte = `<p>Dans le triangle ${nK}${nL}${nM}, l'angle en ${nL} mesure ${angL}° `
        + `et l'angle en ${nM} mesure ${angM}°. Calcule la mesure de l'angle en ${nK}.</p>`;
    const payload = makeSaisie(String(angK), texte, {
        ...opts(L_TABLEUR), strict: true, suffixe: '°',
    });
    payload._meta.generatorKey = 'tableur_texte';
    return payload;
};

const genTableurQcm = () => {
    // Cellules variables (le 3e angle en Bn), pour la variabilité de l'empreinte.
    const r1 = randInt(1, 6);
    let r2 = randInt(1, 6);
    while (r2 === r1) r2 = randInt(1, 6);
    const r3 = randInt(7, 9);
    const c1 = `B${r1}`, c2 = `B${r2}`;
    const correct = `=180-${c1}-${c2}`;
    const choix = [correct, `=${c1}+${c2}-180`, `=180+${c1}+${c2}`, `=180-${c1}*${c2}`];
    const texte = `<p>Dans un tableur, les cellules ${c1} et ${c2} contiennent deux angles d'un triangle. `
        + `Quelle formule saisir en B${r3} pour obtenir automatiquement la mesure du troisième angle ?</p>`;
    const payload = makeQcm(choix, correct, texte, { ...opts(L_TABLEUR) });
    payload._meta.generatorKey = 'tableur_qcm_unique';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Carte 7 — Calculs en chaîne (Ex.6/7/11) : A, D, B alignés
// ═══════════════════════════════════════════════════════════

const genChaine = () => {
    let alpha, beta, gamma1, rep;
    for (let i = 0; i < 200; i++) {
        alpha = randInt(30, 45);
        beta = randInt(78, 92);
        gamma1 = 180 - alpha - beta;              // angle ACD
        rep = 2 * beta + alpha - 180;             // angle ABC
        if (gamma1 >= 20 && rep >= 5) break;
    }
    // Construction EXACTE : A, D, B alignés (horizontale) ; C au-dessus de D.
    const A = { x: 0, y: 0 };
    const D = { x: 130, y: 0 };
    const C = inter(A, alpha, D, 180 - beta);     // rayon +alpha de A, 180−beta de D
    const DC = dist(D, C);
    const toR = (d) => (d * Math.PI) / 180;
    const DB = (DC * Math.sin(toR(gamma1))) / Math.sin(toR(rep));   // loi des sinus
    const B = { x: D.x + DB, y: 0 };
    const cad = cadreur([A, D, B, C], VB, 46);
    const cA = cad(A), cD = cad(D), cB = cad(B), cC = cad(C);
    const fig = creerFigure(VB, salt(alpha, beta, randInt(0, 9999)));
    fig.segment(cA, cB, 'ln1');                    // droite (AB), A-D-B alignés
    fig.segment(cA, cC);
    fig.segment(cC, cD);
    fig.segment(cC, cB);
    fig.point(cA, 'A', 200, 13);
    fig.point(cD, 'D', -90, 13);
    fig.point(cB, 'B', -20, 13);
    fig.point(cC, 'C', 90, 13);
    marqueInterieur(fig, cA, cD, cC, `${alpha}°`, { couleur: VERT, r: 16 });
    marqueInterieur(fig, cD, cA, cC, `${beta}°`, { couleur: VERT, r: 16 });
    marqueEgal(fig, cC, cA, cD);                   // angle ACD
    marqueEgal(fig, cC, cD, cB);                   // angle DCB (= ACD, codés égaux)
    marqueInterieur(fig, cB, cD, cC, '?', { couleur: ROUGE, r: 16 });

    const texte = `<p>Les points A, D et B sont alignés. L'angle en A mesure ${alpha}° `
        + `et l'angle ADC mesure ${beta}°. Les angles ACD et DCB sont égaux (codage). `
        + 'Calcule la mesure de l\'angle ABC (marqué « ? »).</p>';
    const payload = makeSaisie(String(rep), texte, {
        ...opts(L_CHAINE), strict: true, suffixe: '°',
        elements_additionnels: figEl(fig.render()),
    });
    payload._meta.generatorKey = 'chaine_texte';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Carte 8 — Angles et équation (Ex.12)
// ═══════════════════════════════════════════════════════════

const genEquation = () => {
    const [nR, nS, nT] = pickLetters(3);
    const k = randomChoice([9, 12, 15, 18, 21, 24]);
    const a = (180 - k) / 3;                        // R̂ = T̂ = a, Ŝ = a + k ; 3a + k = 180
    // Figure : angles algébriques (classe 'al', non lus par le solveur).
    const { A, B, C } = triangleCadre(a, a);        // A=R(a), B=T(a), C=S(apex a+k)
    const fig = creerFigure(VB, salt(k, randInt(0, 9999)));
    drawTriangle(fig, A, B, C, nR, nT, nS);
    marqueEgal(fig, A, B, C, VERT);                 // R̂ codé
    marqueEgal(fig, B, C, A, VERT);                 // T̂ codé (= R̂)
    marqueInterieur(fig, A, B, C, 'a', { couleur: BLEU, r: 22, rTexte: 30, classeTexte: 'al' });
    marqueInterieur(fig, B, C, A, 'a', { couleur: BLEU, r: 22, rTexte: 30, classeTexte: 'al' });
    marqueInterieur(fig, C, A, B, `a+${k}`, { couleur: BLEU, r: 20, rTexte: 32, classeTexte: 'al' });

    const texte = `<p>Dans le triangle ${nR}${nS}${nT}, ${chapeau(nR)} = a et ${chapeau(nS)} = a + ${k} `
        + `(a en degrés). Les angles en ${nR} et ${nT} sont codés égaux. `
        + 'Calcule la valeur de a.</p>';
    const payload = makeSaisie(String(a), texte, {
        ...opts(L_EQUATION), strict: true, suffixe: '°',
        elements_additionnels: figEl(fig.render()),
    });
    payload._meta.generatorKey = 'equation_texte';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Carte 9 — Le pari d'Aline : alignement par les angles (Ex.9)
// ═══════════════════════════════════════════════════════════

const genAlign = () => {
    let a1, a2, a3, aligned;
    for (let i = 0; i < 200; i++) {
        a1 = randInt(40, 70);
        a2 = randInt(30, 60);
        aligned = Math.random() < 0.5;
        a3 = aligned ? 180 - a1 - a2 : 180 - a1 - a2 + randomChoice([-4, -3, -2, 2, 3, 4]);
        if (a3 >= 25 && a3 <= 110) break;
    }
    // E au centre ; rayons cumulés 0, a1, a1+a2, a1+a2+a3.
    const E = { x: 0, y: 0 };
    const L = 120;
    const r0 = 0, r1 = a1, r2 = a1 + a2, r3 = a1 + a2 + a3;
    const A = ptAt(E, r0, L);
    const P1 = ptAt(E, r1, L);
    const P2 = ptAt(E, r2, L);
    const D = ptAt(E, r3, L);
    const cad = cadreur([E, A, P1, P2, D], VB, 44);
    const cE = cad(E), cA = cad(A), cP1 = cad(P1), cP2 = cad(P2), cD = cad(D);
    const fig = creerFigure(VB, salt(a1, a2, a3, randInt(0, 9999)));
    [cA, cP1, cP2, cD].forEach((P) => fig.segment(cE, P));
    fig.point(cE, 'E', -90, 13);
    fig.point(cA, 'A', r0, 13);
    fig.point(cP1, 'F', r1, 13);
    fig.point(cP2, 'G', r2, 13);
    fig.point(cD, 'D', 180, 13);
    fig.marque(cE, r0, r1, `${a1}°`, { couleur: VERT, r: 20, rTexte: 30 });
    fig.marque(cE, r1, r2, `${a2}°`, { couleur: ROUGE, r: 28, rTexte: 40 });
    fig.marque(cE, r2, r3, `${a3}°`, { couleur: BLEU, r: 36, rTexte: 48 });

    const correct = aligned
        ? 'Oui : la somme des trois angles en E vaut 180°, donc A, E et D sont alignés.'
        : 'Non : la somme des trois angles en E ne vaut pas 180°, donc A, E et D ne sont pas alignés.';
    const distracteurs = [
        'Oui : sur la figure, les points A, E et D semblent alignés.',
        'On ne peut pas savoir sans mesurer plus précisément.',
        'Non : trois angles ne peuvent jamais former un angle plat.',
    ];
    const texte = '<p>La figure n\'est pas en vraie grandeur. Les trois angles de sommet E sont marqués. '
        + 'Les points A, E et D sont-ils alignés ? Choisis la conclusion correctement justifiée.</p>';
    const payload = makeQcm([correct, ...distracteurs], correct, texte, {
        ...opts(L_ALIGN),
        elements_additionnels: figEl(fig.render()),
    });
    payload._meta.generatorKey = 'align_qcm_unique';
    return payload;
};

// ═══════════════════════════════════════════════════════════
// REGISTRY & EXPORTS
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    demo_texte_trou_drag: genDemo,
    calc_fig_texte: genCalcFig,
    calc_sans_texte: genCalcSans,
    partic_texte: genParticSaisie,
    partic_qcm_unique: genParticQcm,
    iso_texte_trou_saisie: genIso,
    tableur_texte: genTableurSaisie,
    tableur_qcm_unique: genTableurQcm,
    chaine_texte: genChaine,
    equation_texte: genEquation,
    align_qcm_unique: genAlign,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', texte: 'Saisie',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'demo', label: L_DEMO, formatsList: buildFormats('demo') },
    { id: 'calc_fig', label: L_CALC_FIG, formatsList: buildFormats('calc_fig') },
    { id: 'calc_sans', label: L_CALC_SANS, formatsList: buildFormats('calc_sans') },
    { id: 'partic', label: L_PARTIC, formatsList: buildFormats('partic') },
    { id: 'iso', label: L_ISO, formatsList: buildFormats('iso') },
    { id: 'tableur', label: L_TABLEUR, formatsList: buildFormats('tableur') },
    { id: 'chaine', label: L_CHAINE, formatsList: buildFormats('chaine') },
    { id: 'equation', label: L_EQUATION, formatsList: buildFormats('equation') },
    { id: 'align', label: L_ALIGN, formatsList: buildFormats('align') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    genDemo, genCalcFig, genCalcSans, genParticSaisie, genParticQcm,
    genIso, genTableurSaisie, genTableurQcm, genChaine, genEquation, genAlign,
};
